import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Save, Plus, Trash2, MoveUp, MoveDown, ChevronRight, ChevronDown, Globe, CornerDownRight, Loader } from 'lucide-react';


const DEFAULT_NAVIGATION = [
    {
        name: 'Solutions',
        path: '/services',
        submenu: [
            { name: 'AI-Augmented Staffing', path: '/services/ai-staffing', icon: 'Zap' },
            { name: 'Cost Optimization', path: '/services/cost-optimization', icon: 'TrendingUp' },
            { name: 'Offshore Strategy', path: '/services/offshore-strategy', icon: 'Globe' },
            { name: 'Digital Transformation', path: '/services/digital-transformation', icon: 'Zap' }
        ]
    },
    {
        name: 'Industries',
        path: '/industries',
        submenu: [
            { name: 'Technology & IT', path: '/industries/technology', icon: 'Zap' },
            { name: 'Healthcare & Medical', path: '/industries/healthcare', icon: 'Award' },
            { name: 'E-commerce & Retail', path: '/industries/retail', icon: 'TrendingUp' },
            { name: 'Financial Services', path: '/industries/finance', icon: 'Award' },
            { name: 'Construction', path: '/industries/construction', icon: 'HardHat' },
            { name: 'Education', path: '/industries/education', icon: 'Book' },
            { name: 'Engineering', path: '/industries/engineering', icon: 'PenTool' },
            { name: 'Human Resources', path: '/industries/hr', icon: 'Users' },
            { name: 'Legal Services', path: '/industries/legal', icon: 'Scale' },
            { name: 'Logistics & Supply Chain', path: '/industries/logistics', icon: 'Truck' },
            { name: 'Manufacturing', path: '/industries/manufacturing', icon: 'Factory' },
            { name: 'Marketing & Advertising', path: '/industries/marketing', icon: 'Megaphone' },
            { name: 'Media & Entertainment', path: '/industries/media', icon: 'Film' },
            { name: 'Operations & Support', path: '/industries/operations', icon: 'Settings' },
            { name: 'Real Estate', path: '/industries/real-estate', icon: 'Home' },
            { name: 'Sales & Biz Dev', path: '/industries/sales', icon: 'Briefcase' },
            { name: 'Travel & Hospitality', path: '/industries/travel', icon: 'Plane' }
        ]
    },
    {
        name: 'Resources',
        path: '/resources',
        submenu: [
            { name: 'BPO Glossary', path: '/glossary', icon: 'BookOpen' },
            { name: 'Case Studies', path: '/case-studies', icon: 'Award' },
            { name: 'ROI Calculator', path: '/calculator', icon: 'Calculator' },
            { name: 'Blog & Insights', path: '/blog', icon: 'BookOpen' }
        ]
    },
    {
        name: 'Training',
        path: '/training',
        submenu: [
            { name: 'AI-Augmented BD Program', path: '/training/ai-bd', icon: 'Zap' },
            { name: 'Experienced BD Track', path: '/training/experienced-bd', icon: 'Award' },
            { name: 'Aspiring BD Track', path: '/training/aspiring-bd', icon: 'Users' },
            { name: 'Webinars & Events', path: '/training/webinars', icon: 'Users' }
        ]
    },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' }
];

const MenuManager = () => {
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expandedItems, setExpandedItems] = useState({});

    useEffect(() => {
        fetchMenu();
    }, []);

    const fetchMenu = async () => {
        setLoading(true);
        try {
            const docRef = doc(db, 'settings', 'navigation');
            const docSnap = await getDoc(docRef);
            if (docSnap.exists() && docSnap.data().items) {
                setMenuItems(docSnap.data().items);
            } else {
                setMenuItems([]); // Or keep generic empty state
            }
        } catch (error) {
            console.error("Error fetching menu:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'settings', 'navigation'), { items: menuItems }, { merge: true });
            alert("Menu saved successfully!");
        } catch (error) {
            console.error("Error saving menu:", error);
            alert("Failed to save menu.");
        } finally {
            setSaving(false);
        }
    };

    const handleSeedDefault = () => {
        if (window.confirm("This will overwrite your current menu with the system default. Are you sure?")) {
            setMenuItems(DEFAULT_NAVIGATION);
        }
    };

    const toggleExpand = (index) => {
        setExpandedItems(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const updateItem = (index, field, value) => {
        const newItems = [...menuItems];
        newItems[index] = { ...newItems[index], [field]: value };
        setMenuItems(newItems);
    };

    const updateSubItem = (parentIndex, subIndex, field, value) => {
        const newItems = [...menuItems];
        newItems[parentIndex].submenu[subIndex] = { ...newItems[parentIndex].submenu[subIndex], [field]: value };
        setMenuItems(newItems);
    };

    const addTopLevelItem = () => {
        setMenuItems([...menuItems, { name: 'New Item', path: '/', submenu: [] }]);
    };

    const addSubItem = (index) => {
        const newItems = [...menuItems];
        if (!newItems[index].submenu) newItems[index].submenu = [];
        newItems[index].submenu.push({ name: 'New Subitem', path: '/', icon: 'Circle' });
        setMenuItems(newItems);
        if (!expandedItems[index]) toggleExpand(index);
    };

    const deleteItem = (index) => {
        if (window.confirm("Delete this item?")) {
            const newItems = menuItems.filter((_, i) => i !== index);
            setMenuItems(newItems);
        }
    };

    const deleteSubItem = (parentIndex, subIndex) => {
        if (window.confirm("Delete this sub-item?")) {
            const newItems = [...menuItems];
            newItems[parentIndex].submenu = newItems[parentIndex].submenu.filter((_, i) => i !== subIndex);
            setMenuItems(newItems);
        }
    };

    const moveItem = (index, direction) => {
        if (direction === 'up' && index > 0) {
            const newItems = [...menuItems];
            [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
            setMenuItems(newItems);
        }
        if (direction === 'down' && index < menuItems.length - 1) {
            const newItems = [...menuItems];
            [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
            setMenuItems(newItems);
        }
    };

    if (loading) return <div className="p-8 text-center text-[var(--muted)]">Loading menu configuration...</div>;

    return (
        <div className="panel active">
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">Settings</div>
                    <h1>Navigation Menu <span>.</span></h1>
                </div>
                <div className="page-hdr-actions">
                    <Link to="/admin/dashboard" className="btn btn-ghost" style={{ borderColor: 'var(--border)', color: 'var(--text2)', marginRight: '8px' }}>
                        Dashboard
                    </Link>
                    <button
                        onClick={handleSeedDefault}
                        className="btn btn-ghost"
                    >
                        Reset to Default
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="btn btn-primary"
                    >
                        {saving ? <Loader className="animate-spin" size={14} /> : <Save size={14} />} Save Changes
                    </button>
                </div>
            </div>

            <div className="space-y-4 max-w-4xl">
                {menuItems.length === 0 && (
                    <div className="text-center py-12 border-2 border-dashed border-[var(--border)] rounded-lg">
                        <p className="text-[var(--muted)] mb-4">No menu items found.</p>
                        <button onClick={handleSeedDefault} className="btn btn-primary">
                            Load Default Menu Structure
                        </button>
                    </div>
                )}

                {menuItems.map((item, index) => (
                    <div key={index} className="border border-[var(--border)] rounded-lg p-3 bg-[var(--surface2)]">
                        {/* Top Level Item Row */}
                        <div className="flex items-center gap-3">
                            <button onClick={() => toggleExpand(index)} className="text-[var(--muted)] hover:text-[var(--text)]">
                                {expandedItems[index] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                            </button>

                            <div className="flex-1 grid grid-cols-12 gap-2">
                                <div className="col-span-4">
                                    <input
                                        type="text"
                                        value={item.name}
                                        onChange={(e) => updateItem(index, 'name', e.target.value)}
                                        className="form-input w-full p-2 text-sm"
                                        placeholder="Label"
                                    />
                                </div>
                                <div className="col-span-5 flex items-center gap-1">
                                    <Globe size={14} className="text-[var(--muted)]" />
                                    <input
                                        type="text"
                                        value={item.path}
                                        onChange={(e) => updateItem(index, 'path', e.target.value)}
                                        className="form-input w-full p-2 text-sm font-mono text-xs"
                                        placeholder="/path"
                                    />
                                </div>
                                <div className="col-span-3 flex justify-end gap-1">
                                    <button onClick={() => moveItem(index, 'up')} disabled={index === 0} className="p-1 text-[var(--muted)] hover:text-[var(--text)] disabled:opacity-30">
                                        <MoveUp size={14} />
                                    </button>
                                    <button onClick={() => moveItem(index, 'down')} disabled={index === menuItems.length - 1} className="p-1 text-[var(--muted)] hover:text-[var(--text)] disabled:opacity-30">
                                        <MoveDown size={14} />
                                    </button>
                                    <button onClick={() => addSubItem(index)} className="p-1 text-[var(--accent)] hover:text-[var(--accent-hover)]" title="Add Submenu">
                                        <Plus size={14} />
                                    </button>
                                    <button onClick={() => deleteItem(index)} className="p-1 text-[var(--accent3)] hover:text-red-500">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Submenu Items */}
                        {expandedItems[index] && item.submenu && item.submenu.length > 0 && (
                            <div className="mt-2 ml-8 space-y-2 border-l-2 border-[var(--border)] pl-3 py-1">
                                {item.submenu.map((subItem, subIndex) => (
                                    <div key={subIndex} className="flex items-center gap-2">
                                        <CornerDownRight size={14} className="text-[var(--muted)]" />
                                        <div className="flex-1 grid grid-cols-12 gap-2">
                                            <div className="col-span-4">
                                                <input
                                                    type="text"
                                                    value={subItem.name}
                                                    onChange={(e) => updateSubItem(index, subIndex, 'name', e.target.value)}
                                                    className="form-input w-full p-1 text-xs"
                                                    placeholder="Sub-label"
                                                />
                                            </div>
                                            <div className="col-span-5">
                                                <input
                                                    type="text"
                                                    value={subItem.path}
                                                    onChange={(e) => updateSubItem(index, subIndex, 'path', e.target.value)}
                                                    className="form-input w-full p-1 text-xs font-mono"
                                                    placeholder="/sub-path"
                                                />
                                            </div>
                                            <div className="col-span-3 flex justify-end">
                                                <button onClick={() => deleteSubItem(index, subIndex)} className="p-1 text-[var(--accent3)] hover:text-red-500">
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}

                <button
                    onClick={addTopLevelItem}
                    className="w-full py-2 border-2 border-dashed border-[var(--border)] rounded-lg text-[var(--muted)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                >
                    <Plus size={16} /> Add Top Level Menu Item
                </button>
            </div>
        </div>
    );
};

export default MenuManager;
